import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Database, Search, FileText, ShieldCheck, Rocket, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Undo2 } from "lucide-react";

function Promote2027Panel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [staging, setStaging] = useState(false);

  const load = useCallback(async () => {
    setBusy("status");
    try { const { data } = await api.get("/admin/plan-intel/promote-2027/status"); setStatus(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load promotion status"); }
    finally { setBusy(""); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!staging) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/plan-intel/promote-2027/status");
        setStatus(data);
        const last = (data.recent_runs || []).find((r) => r.action === "stage");
        if (last && ["staged", "validation_failed", "error"].includes(last.status)) {
          setStaging(false);
          if (last.status === "staged") toast.success("Staging complete — review and activate.");
          else toast.error(`Staging ${last.status}${last.error ? ": " + last.error : ""}`);
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [staging]);

  const stage = async () => {
    setBusy("stage");
    try { await api.post("/admin/plan-intel/promote-2027/stage"); setStaging(true); toast.info("Staging started — re-importing 2027 from CMS (about 1–2 minutes)."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start staging"); }
    finally { setBusy(""); }
  };

  const activate = async () => {
    if (!window.confirm("Activate the staged CY2027 dataset in this environment? 2026 data is untouched and rollback is available.")) return;
    setBusy("activate");
    try {
      const stageRun = (status?.recent_runs || []).find((r) => r.action === "stage" && r.status === "staged");
      const { data } = await api.post("/admin/plan-intel/promote-2027/activate", { version: stageRun?.version || null });
      if (data.ok) toast.success(`Activated ${data.version} — ${data.active_2027_plans} plans, ${data.active_2027_benefits} benefit rows live.`);
      else toast.error(data.reason || "Activation aborted — stage the import first.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
    finally { setBusy(""); }
  };

  const rollback = async () => {
    if (!window.confirm("Roll back CY2027 (deactivate) and restore the prior active import? 2026 untouched.")) return;
    setBusy("rollback");
    try { await api.post("/admin/plan-intel/promote-2027/rollback"); toast.success("Rolled back."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
    finally { setBusy(""); }
  };

  const v = status?.validation || {};
  const activeP = status?.active_2027_plans ?? 0;
  const activeB = status?.active_2027_benefits ?? 0;
  const validated = !!status?.validation_ok;
  const isLive = activeP > 0;

  return (
    <div className="rounded-xl border-2 border-[#0B192C] bg-white p-4 mb-6" data-testid="promote-2027-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-bold text-[#0B192C] flex items-center gap-2"><Rocket className="w-4 h-4" /> CY2027 Production Promotion</div>
        <div className="flex items-center gap-2">
          {isLive ? <Badge className="bg-emerald-600">2027 ACTIVE</Badge> : <Badge variant="secondary">2027 not active</Badge>}
          <Button size="sm" variant="outline" onClick={load} disabled={!!busy} data-testid="promote-refresh"><RefreshCw className={`w-4 h-4 ${busy === "status" ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>
      <p className="text-xs text-[#6B7280] mt-1">Runs the idempotent CMS re-import + activation in THIS environment (production when live). 2026 data, clients and secrets are untouched; rollback is available.</p>

      <div className="mt-3 grid sm:grid-cols-4 gap-3 text-sm" data-testid="promote-counts">
        <div><span className="text-[#6B7280]">Validation</span><br/><b className={(validated || isLive) ? "text-emerald-700" : "text-amber-700"}>{validated ? "passed" : (isLive ? "active" : "not ready")}</b></div>
        <div><span className="text-[#6B7280]">Staged segments</span><br/><b>{v.staged_segments ?? "—"}</b></div>
        <div><span className="text-[#6B7280]">Active 2027 plans</span><br/><b data-testid="promote-active-plans">{activeP}</b></div>
        <div><span className="text-[#6B7280]">Active 2027 benefit rows</span><br/><b data-testid="promote-active-benefits">{activeB}</b></div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={stage} disabled={!!busy || staging} data-testid="promote-stage">
          {staging ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Staging…</> : <>Stage 2027 import</>}
        </Button>
        <Button onClick={activate} disabled={!!busy || staging || !validated} className="bg-emerald-700 hover:bg-emerald-800" data-testid="promote-activate">
          {busy === "activate" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Activate 2027{isLive ? " (re-activate)" : ""}
        </Button>
        {isLive && (
          <Button onClick={rollback} disabled={!!busy} variant="outline" className="text-red-700 border-red-300" data-testid="promote-rollback"><Undo2 className="w-4 h-4 mr-1.5" />Rollback</Button>
        )}
      </div>
      {!validated && !isLive && <p className="mt-2 text-xs text-amber-700 flex items-center gap-1" data-testid="promote-validation-note"><AlertTriangle className="w-3.5 h-3.5" /> Stage the import first — activation stays disabled until validation passes.</p>}
      {isLive && <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1" data-testid="promote-live-note"><CheckCircle2 className="w-3.5 h-3.5" /> CY2027 is live in this environment. To refresh from CMS, Stage again then re-activate.</p>}

      {(status?.recent_runs || []).length > 0 && (
        <div className="mt-3 text-[11px] text-[#6B7280]" data-testid="promote-runs">
          {status.recent_runs.slice(0, 4).map((r, i) => (
            <div key={i}>{r.created_at?.slice(0, 19)?.replace("T", " ")} · {r.action} · {r.status}{r.version ? ` · ${r.version}` : ""}{r.started_by ? ` · ${r.started_by}` : ""}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  normalized: "bg-emerald-100 text-emerald-800",
  offered_only: "bg-amber-100 text-amber-800",
  not_present: "bg-gray-100 text-gray-600",
};

const money = (v) => (v === 0 || v ? `$${v}` : "—");

function CostSharing({ n }) {
  if (!n) return <span className="text-[#6B7280]">—</span>;
  const bits = [];
  if (n.copay && n.copay.applies) bits.push(`Copay ${money(n.copay.amount)}`);
  if (n.copay_flat && n.copay_flat.applies) bits.push(`Copay ${money(n.copay_flat.amount)}`);
  if (n.coinsurance && n.coinsurance.applies) bits.push(`Coinsurance ${n.coinsurance.percent ?? "?"}%`);
  if (n.max_coverage && n.max_coverage.amount) bits.push(`Max ${money(n.max_coverage.amount)}/${n.max_coverage.periodicity || "yr"}`);
  if (n.amount != null && n.period) bits.push(`${money(n.amount)}/${n.period === "monthly" ? "mo" : n.period}`);
  if (Array.isArray(n.day_intervals)) {
    n.day_intervals.forEach((d) =>
      bits.push(`Days ${d.begin_day}${d.end_day ? `–${d.end_day}` : "+"}: ${money(d.copay)}`));
  }
  if (n.in_network_amount != null) bits.push(`In-network ${money(n.in_network_amount)}`);
  return <span>{bits.length ? bits.join(" · ") : "structure present"}</span>;
}

export default function PlanIntel() {
  const [org, setOrg] = useState("");
  const [results, setResults] = useState([]);
  const [q, setQ] = useState({ plan_year: 2026, contract_id: "", plan_id: "", segment_id: "000" });
  const [detail, setDetail] = useState(null);
  const [rawKey, setRawKey] = useState(null);
  const [err, setErr] = useState("");

  const search = async () => {
    setErr("");
    const r = await api.get(`/admin/plan-intel/plans`, { params: { org, limit: 25 } });
    setResults(r.data || []);
  };
  const resolve = async (over) => {
    setErr(""); setDetail(null); setRawKey(null);
    const params = { ...q, ...(over || {}) };
    try {
      const r = await api.get(`/admin/plan-intel/plan`, { params });
      setDetail(r.data);
      setQ((s) => ({ ...s, ...params }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Plan not found in the active dataset");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="plan-intel-page">
      <PageHeader title="Plan Intelligence QA" icon={Database}
        subtitle="Internal tool — resolve an exact CMS plan → normalized benefits → provenance. Not consumer plan shopping." />

      <Promote2027Panel />

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {/* Search by organization */}
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
          <div className="text-xs font-semibold text-[#6B7280] flex items-center gap-2"><Search className="w-4 h-4" />Find plans by organization</div>
          <div className="flex gap-2 mt-2">
            <Input data-testid="pi-org-input" value={org} onChange={(e) => setOrg(e.target.value)}
              placeholder="e.g. Devoted, CarePlus, Humana" onKeyDown={(e) => e.key === "Enter" && search()} />
            <Button data-testid="pi-org-search" onClick={search}>Search</Button>
          </div>
          <div className="mt-3 max-h-64 overflow-auto divide-y" data-testid="pi-search-results">
            {results.map((p) => (
              <button key={`${p.contract_id}-${p.plan_id}-${p.segment_id}`} data-testid="pi-search-row"
                onClick={() => resolve({ plan_year: p.plan_year, contract_id: p.contract_id, plan_id: p.plan_id, segment_id: p.segment_id })}
                className="w-full text-left py-2 hover:bg-[#F8F8F5] px-1">
                <div className="text-sm font-semibold text-[#0B192C]">{p.plan_name || "—"}</div>
                <div className="text-xs text-[#6B7280]">{p.contract_id}-{p.plan_id}-{p.segment_id} · {p.organization_marketing_name} · {p.plan_type_label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Resolve by identity */}
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
          <div className="text-xs font-semibold text-[#6B7280]">Resolve by identity (year · contract · PBP · segment)</div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Input data-testid="pi-year" value={q.plan_year} onChange={(e) => setQ({ ...q, plan_year: e.target.value })} placeholder="Year" />
            <Input data-testid="pi-contract" value={q.contract_id} onChange={(e) => setQ({ ...q, contract_id: e.target.value })} placeholder="H1019" />
            <Input data-testid="pi-pbp" value={q.plan_id} onChange={(e) => setQ({ ...q, plan_id: e.target.value })} placeholder="001" />
            <Input data-testid="pi-segment" value={q.segment_id} onChange={(e) => setQ({ ...q, segment_id: e.target.value })} placeholder="000" />
          </div>
          <Button data-testid="pi-resolve" className="mt-3 w-full" onClick={() => resolve()}>Resolve plan</Button>
          {err && <p className="mt-2 text-sm text-red-600" data-testid="pi-error">{err}</p>}
        </div>
      </div>

      {detail && (
        <div className="mt-6 space-y-4" data-testid="pi-detail">
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display text-xl text-[#0B192C]" data-testid="pi-plan-name">{detail.plan.plan_name}</h3>
                <p className="text-sm text-[#6B7280]">{detail.plan.organization_marketing_name} · {detail.plan.contract_legal_name}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary">{detail.plan.contract_id}-{detail.plan.plan_id}-{detail.plan.segment_id}</Badge>
                <Badge variant="secondary">{detail.plan.plan_type_label}</Badge>
                {detail.plan.is_snp === "Yes" && <Badge>SNP: {detail.plan.snp_type}</Badge>}
                {detail.plan.is_employer_only === "Yes" && <Badge className="bg-amber-200 text-amber-900">EGHP</Badge>}
              </div>
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
              <div><span className="text-[#6B7280]">MOOP (in-network):</span> <b>{money(detail.plan.moop?.in_network_amount)}</b></div>
              <div><span className="text-[#6B7280]">Service area:</span> <b>{detail.service_area_count}</b> FL county rows</div>
              <div><span className="text-[#6B7280]">Offers Rx:</span> <b>{detail.plan.is_offer_rx}</b></div>
            </div>
          </div>

          {/* Benefits */}
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden">
            <div className="px-4 py-3 bg-[#F8F8F5] border-b font-bold text-[#0B192C] text-sm flex items-center gap-2"><FileText className="w-4 h-4" />Normalized benefits</div>
            <table className="w-full text-sm" data-testid="pi-benefits-table">
              <thead><tr className="text-left text-[#6B7280]"><th className="p-3">Benefit</th><th className="p-3">Code</th><th className="p-3">Status</th><th className="p-3">Stated cost-sharing</th><th className="p-3"></th></tr></thead>
              <tbody>
                {(detail.benefits || []).map((b) => (
                  <React.Fragment key={b.benefit_key}>
                    <tr className="border-t border-[#EEE]" data-testid={`pi-benefit-${b.benefit_key}`}>
                      <td className="p-3 font-medium text-[#0B192C]">{b.label}</td>
                      <td className="p-3 text-[#6B7280]">{b.category_code || "—"}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[b.normalization_status] || ""}`}>{b.normalization_status}</span></td>
                      <td className="p-3"><CostSharing n={b.normalized} /></td>
                      <td className="p-3">{b.raw_source && <button className="text-xs underline text-[#0B60B0]" data-testid={`pi-raw-toggle-${b.benefit_key}`} onClick={() => setRawKey(rawKey === b.benefit_key ? null : b.benefit_key)}>{rawKey === b.benefit_key ? "hide raw" : "raw"}</button>}</td>
                    </tr>
                    {rawKey === b.benefit_key && (
                      <tr className="bg-[#0B192C]"><td colSpan="5" className="p-3">
                        <div className="text-[11px] text-emerald-300 mb-1">{b.raw_source_path}</div>
                        <pre className="text-xs text-gray-200 overflow-auto max-h-64" data-testid="pi-raw-json">{JSON.stringify(b.raw_source, null, 1)}</pre>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Provenance */}
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid="pi-provenance">
            <div className="text-sm font-bold text-[#0B192C] flex items-center gap-2"><ShieldCheck className="w-4 h-4" />CMS provenance</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs text-[#374151]">
              <div>Dataset: <b>{detail.provenance?.plan_provenance?.dataset}</b></div>
              <div>Source file: <b>{detail.provenance?.plan_provenance?.source_file}</b></div>
              <div>Vintage: <b>{detail.provenance?.plan_provenance?.publication_vintage}</b></div>
              <div>Parser / norm: <b>{detail.provenance?.plan_provenance?.parser_version} / {detail.provenance?.plan_provenance?.normalization_version}</b></div>
              <div className="sm:col-span-2 break-all">Source URL: {detail.provenance?.plan_provenance?.source_url}</div>
              <div className="sm:col-span-2">Raw CMS identity: <code>{JSON.stringify(detail.provenance?.raw_plan_identity)}</code></div>
              <div className="sm:col-span-2">Import run: {detail.provenance?.import_run?.import_id} ({detail.provenance?.import_run?.status})</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
